export type FileKind = 'image' | 'video' | 'pdf' | 'docx' | 'doc' | 'spreadsheet';

const VIDEO_EXTENSIONS = ['mp4', 'mov', 'avi', 'mkv', 'webm', 'm4v'];

/** Works for normal URLs (incl. signed URLs with ?query), data: URIs and blob: URLs (via override). */
export const getExtension = (src: string, override?: string): string => {
  if (override) return override.replace('.', '').toLowerCase();
  if (!src) return '';

  if (src.startsWith('data:')) {
    const mime = (src.slice(5).split(/[;,]/)[0] || '').toLowerCase();
    if (mime === 'application/pdf') return 'pdf';
    if (mime.includes('wordprocessingml')) return 'docx';
    if (mime.includes('msword')) return 'doc';
    if (mime.includes('spreadsheet') || mime.includes('excel')) return 'xlsx';
    if (mime.startsWith('video/')) return 'mp4';
    return mime.split('/')[1] || '';
  }

  const clean = src.split('#')[0].split('?')[0];
  const last = clean.split('/').pop() || '';
  return last.includes('.') ? (last.split('.').pop() as string).toLowerCase() : '';
};

export const getFileKind = (src: string, override?: string): FileKind => {
  const ext = getExtension(src, override);
  if (ext === 'pdf') return 'pdf';
  if (ext === 'docx') return 'docx';
  if (ext === 'doc') return 'doc';
  if (ext === 'xls' || ext === 'xlsx') return 'spreadsheet';
  if (VIDEO_EXTENSIONS.includes(ext)) return 'video';
  return 'image';
};

/** Chrome blocks window.open(data:...), so we always go through a blob URL. */
export const openInNewTab = async (src: string, kind?: FileKind) => {
  try {
    const res = await fetch(src);
    const blob = await res.blob();
    const typed = kind === 'pdf' ? new Blob([blob], { type: 'application/pdf' }) : blob;
    window.open(URL.createObjectURL(typed), '_blank');
  } catch {
    window.open(src, '_blank', 'noopener');
  }
};

/** Downloads a file through a blob URL so it works for data:, blob: and cross-origin URLs. */
export const downloadFile = async (src: string, fileName: string) => {
  try {
    const res = await fetch(src);
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch {
    const a = document.createElement('a');
    a.href = src;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
};

/** Office Online can only fetch URLs that are reachable from the internet. */
export const isPublicHttpUrl = (src: string): boolean =>
  /^https?:\/\//i.test(src) &&
  !/^https?:\/\/(localhost|127\.|10\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.)/i.test(src);
